import React, { useRef, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import DataTableCard from '../../../components/ui/DataTableCard';
import apiClient from '../../../services/api-client';
import StorageHelper from '../../../utils/storage-helper';

const toIsoDate = (date) => date.toISOString().split('T')[0];

const describeError = (error, labels) => {
    const status = error.response?.status;
    if (status === 404) return error.response.data?.detail || labels.notFound;
    if (status === 400) return error.response.data?.detail || 'Invalid request.';
    if (status === 500) return 'Server error. Please contact support.';
    if (error.response) return labels.failure;
    if (error.request) return 'No response from server. Check your connection.';
    return labels.failure;
};

/**
 * A report fetched from `endpoint` for a date range plus one extra filter field, shown in a DataTableCard.
 *
 * param:  { name, label, numeric?, required? }  - the extra query parameter (e.g. agentId, serialNumber)
 * labels: { subject, notFound, failure, success, emptyMessage }
 */
const DateRangeReportTable = ({ title, exportName, endpoint, param, columns, labels, useParamAsFilename = false }) => {
    const toast = useRef(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(() => new Date());
    const [toDate, setToDate] = useState(() => new Date());
    const [paramValue, setParamValue] = useState('');

    const showToast = (severity, summary, detail) => toast.current.show({severity, summary, detail, life: 3000});

    const generate = async () => {
        const value = paramValue.trim();
        if (param.required && !value) {
            showToast('warn', `Missing ${param.label}`, `Please enter a ${param.label.toLowerCase()}`);
            return;
        }

        setLoading(true);
        try {
            const params = {from: toIsoDate(fromDate ?? new Date()), to: toIsoDate(toDate ?? new Date())};
            if (value) params[param.name] = value;

            const response = await apiClient.get(endpoint, {
                params,
                headers: {Authorization: 'Bearer ' + StorageHelper.get('token')}
            });

            if (response.data.status === 'SUCCESS') {
                setRows(response.data.responseBody);
                showToast('success', 'Success', labels.success);
            } else {
                showToast('error', 'Error', labels.failure);
            }
        } catch (error) {
            console.error(error);
            showToast('error', 'Error', describeError(error, labels));
        } finally {
            setLoading(false);
        }
    };

    const filters = (
        <div className="flex flex-col md:flex-row md:items-end gap-3 mb-6 bg-base-100/50 p-4 rounded-xl border border-base-200">
            <div className="flex flex-col gap-1">
                <label htmlFor="reportParam" className="text-xs font-bold text-slate-700">{param.label}</label>
                <InputText
                    id="reportParam"
                    value={paramValue}
                    onChange={(e) => setParamValue(param.numeric ? e.target.value.replace(/\D/g, '') : e.target.value)}
                    placeholder={param.label}
                    inputMode={param.numeric ? 'numeric' : undefined}
                    className="input input-bordered h-10 w-full md:w-48"
                />
            </div>
            {[['fromDate', 'From Date', fromDate, setFromDate], ['toDate', 'To Date', toDate, setToDate]].map(([id, label, value, setValue]) => (
                <div key={id} className="flex flex-col gap-1">
                    <label htmlFor={id} className="text-xs font-bold text-slate-700">{label}</label>
                    <Calendar
                        inputId={id}
                        value={value}
                        onChange={(e) => setValue(e.value)}
                        dateFormat="yy-mm-dd"
                        placeholder={label}
                        inputClassName="input input-bordered h-10 w-full md:w-36"
                    />
                </div>
            ))}
            <button className="btn btn-primary text-white font-semibold" onClick={generate} disabled={loading}>
                {loading ? <span className="loading loading-spinner loading-sm"></span> : <i className="pi pi-sync"></i>}
                Generate
            </button>
        </div>
    );

    return (
        <>
            <Toast ref={toast}/>
            <DataTableCard
                title={title}
                exportName={exportName}
                exportFilename={useParamAsFilename ? paramValue : undefined}
                columns={columns}
                rows={rows}
                loading={loading}
                rowsPerPage={10}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                emptyMessage={labels.emptyMessage}
                filters={filters}
            />
        </>
    );
};

export default DateRangeReportTable;
